/**
 * useAutoExpandSections - Hook for Auto-Expanding Sections
 *
 * Manages automatic expansion of workflow sections when previous sections become complete.
 * Extracted from ProjectSetupPage to prevent infinite loop issues and improve separation of concerns.
 *
 * Usage:
 * ```tsx
 * const { expandedSections, setExpandedSections } = useAutoExpandSections(sectionStatuses, {
 *   dates: true,
 *   tax: false,
 *   millage: false,
 *   drf: false
 * });
 * ```
 */

import { useEffect, useRef, useState } from 'react';

import type { SectionStatus, WorkflowStatuses } from '~/hooks/useWorkflowStatus';

interface SectionConfig {
  key: string;
  status: SectionStatus;
  enabled: boolean;
}

interface UseAutoExpandSectionsOptions {
  dates: boolean;
  tax: boolean;
  millage: boolean;
  drf: boolean;
}

/**
 * Hook to manage auto-expansion of sections based on completion status
 *
 * @param sectionStatuses - Current status of all workflow sections
 * @param initialExpanded - Initial expanded state for each section
 * @returns Object with expandedSections state and setExpandedSections setter
 */
export const useAutoExpandSections = (
  sectionStatuses: WorkflowStatuses,
  initialExpanded: UseAutoExpandSectionsOptions
) => {
  const [expandedSections, setExpandedSections] =
    useState<UseAutoExpandSectionsOptions>(initialExpanded);

  // Track previous statuses to detect when a section becomes complete
  const prevStatusesRef = useRef<Record<string, SectionStatus>>({
    dates: 'empty',
    tax: 'empty',
    millage: 'empty',
    drf: 'empty',
  });

  // Ref to track expanded sections without including in useEffect dependencies
  const expandedSectionsRef = useRef<UseAutoExpandSectionsOptions>(expandedSections);
  expandedSectionsRef.current = expandedSections;

  // Extract status values as primitives to ensure stable dependencies
  const datesStatus = sectionStatuses.dates;
  const taxStatus = sectionStatuses.tax;
  const millageStatus = sectionStatuses.millage;
  const drfStatus = sectionStatuses.drf;

  // Auto-open next section when current section becomes complete
  useEffect(() => {
    // Calculate enabled flags directly from status values
    const datesEnabled = true;
    const taxEnabled = datesStatus === 'complete';
    const millageEnabled = datesStatus === 'complete' && taxStatus === 'complete';
    const drfEnabled =
      datesStatus === 'complete' && taxStatus === 'complete' && millageStatus === 'complete';

    const sections: SectionConfig[] = [
      { key: 'dates', status: datesStatus, enabled: datesEnabled },
      { key: 'tax', status: taxStatus, enabled: taxEnabled },
      { key: 'millage', status: millageStatus, enabled: millageEnabled },
      { key: 'drf', status: drfStatus, enabled: drfEnabled },
    ];

    sections.forEach((section, index) => {
      const prevStatus = prevStatusesRef.current[section.key];
      // If this section just became complete (wasn't complete before, is complete now)
      if (prevStatus !== 'complete' && section.status === 'complete' && section.enabled) {
        const nextSection = sections.slice(index + 1).find((s) => s.enabled);
        // Use ref to check expanded state without including in dependencies
        if (
          nextSection &&
          !expandedSectionsRef.current[nextSection.key as keyof UseAutoExpandSectionsOptions]
        ) {
          setExpandedSections((prev) => ({
            ...prev,
            [nextSection.key]: true,
          }));
        }
      }
      // Update previous status
      prevStatusesRef.current[section.key] = section.status;
    });
    // Depend only on primitive status values, not the object
  }, [datesStatus, taxStatus, millageStatus, drfStatus]);

  return { expandedSections, setExpandedSections };
};
